import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import clsx from 'clsx';
import TemplateListView from 'src/views/templates/TemplateListView';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  CardMedia,
  Dialog, Grid
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { setLstUsedTemplate, setDefaultSelectedTemplate } from '../../../redux/reducers/templates/actions';
import { getTemplates } from '../../../redux/reducers/templates/selectors';
import CategoryApi from '../../../utils/apiManager/constrollers/categoryApi';
import {
  addUploadedImage,
  deleteUploadedImage,
  addImages,
  addUploadingImages,
  addProcessingImage,
  addProcessedImage,
} from '../../../redux/reducers/images/actions';
import ImageApi from '../../../utils/apiManager/constrollers/imageApi';
import { updateCategoryCount } from '../../../redux/reducers/categories/actions';
import { calculateObjectsTypes, getCurrentLanguage } from '../../../utils/utils';
import sendMetric from '../../help/googleMetric';

const styles = (theme) => ({
  root: {
    alignItems: 'center',
  },
  cardContent: {
    padding: '0',
    margin: '0',
    '&:last-child': {
      paddingBottom: 0
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    }
  },
  uploadArea: {
    width: '100%',
    opacity: 0.7,
    '&:hover': {
      opacity: 1,
      cursor: 'pointer'
    }
  },
  addBox: {
    alignItems: 'center',
    display: 'flex',
    width: '100%',
    margin: '30px auto 15px auto'
  },
  media: {
    height: 142,
    padding: '15px 15px 15px 15px',
    [theme.breakpoints.down('xs')]: {
      height: '190px',
      margin: '15px 15px 15px 15px',
    },
    // paddingTop: '52.25%', // 16:9
    borderRadius: '5px 5px 0 0',
  },
  boxActiveTemplate: {
    float: 'left',
    padding: '4px 0 0 0',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    height: '35px',
    width: '100%',
    color: '#757575',
    marginTop: '-45px',
    textAlign: 'center'
  },
  templateThumb: {
    margin: '0 4px 0px 4px',
    cursor: 'pointer',
    width: 47,
    height: 47,
    display: 'flex',
    border: '1px solid',
    borderColor: '#dddee1',
    backgroundColor: '#00000',
  },
  templateText: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#000000'
  },
  textLatest: {
    display: 'block',
    margin: '5px 0 0 22px',
    float: 'left',
    fontWeight: 300,
    color: '#000000'
  },
  textUpload: {
    margin: '8px 0 0 0',
    display: 'block',
    fontSize: '16px',
    fontWeight: 500,
    color: '#000000'
  },
  textTemplateName: {
    margin: '0 0 5px 0',
    display: 'block',
    fontSize: '18px',
    fontWeight: 500,
    color: '#000000'
  },
  tmpBox: {
    opacity: 0.7,
    '&:hover': {
      borderColor: '#dddee1',
      boxShadow: '1px',
      opacity: 1
    }
  },
  filesArea: {
    display: 'flex',
    textAlign: 'center',
    border: 0,
    borderRadius: '7px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    [theme.breakpoints.down('xs')]: {
      height: '157px',
    },
    flexFlow: 'column',
    justifyContent: 'center',
    alignContent: 'space-between',
  },
  dialogTitle: {
    padding: '15px 0 0 20px',
    textAlign: 'center'
  },
  uploadIcon: {
    fontSize: 45,
    padding: 0,
    color: 'rgb(44, 56, 126)',
    margin: 'auto',
  }
});

class UploadImageTemplateCard extends React.Component {
  constructor(props) {
    super(props);
    this.hiddenFileInput = React.createRef();
    this.handleClickUpload = this.handleClickUpload.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.getPropOfDefaultTemplate = this.getPropOfDefaultTemplate.bind(this);
    this.updateImageObjects = this.updateImageObjects.bind(this);
  }

  async componentDidMount() {
    this.categoryApi = await CategoryApi.getInstance();
    this.imagesApi = await ImageApi.getInstance();
    const token = localStorage.getItem('token');
    const userId = this.categoryApi.userId || localStorage.getItem('userId');
    this.eventSource = new EventSource(`${window['apiUrl']}/user/${userId}/sse?access_token=${token}`);
    this.eventSource.addEventListener('image', this.updateImageObjects);
  }

  handleClickUpload() {
    this.hiddenFileInput.current.click();
  }

  async updateImageObjects(e) {
    const data = JSON.parse(e.data);
    if (data.id) {
      const imgTemplate = this.props.templates.find((el) => {
        return el.id === data.templateId;
      });
      const detectedObjectsSummary = calculateObjectsTypes(
        data.detectedObjects,
        getCurrentLanguage(),
        imgTemplate,
      );
      this.props.addProcessedImage({
        ...data,
        detectedObjectsSummary,
      });
      this.props.updateCategoryCount('increase', detectedObjectsSummary?.[0]?.count, this.props.lastUsedGroupId);
    }
  }

  async handleChange(event) {
    event.preventDefault();
    const filesUploaded = event.target.files;
    const defTemplate = this.props.templates.find((el) => {
      return el.id === this.props.defaultSelectedTemplateId;
    });

    const preparedFiles = [];

    for (const file of filesUploaded) {
      preparedFiles.push({
        fileId: v4(),
        fileData: file,
      });
    }

    if (preparedFiles.length) {
      this.props.addUploadingImages(
        preparedFiles.map((prepFile) => ({ fileId: prepFile.fileId, isUploading: true })),
      );
    }

    for (const file of preparedFiles) {
      this.props.setLstUsedTemplate(this.props.defaultSelectedTemplateId, defTemplate);
      const preparedImage = await this.resize(file.fileData, defTemplate.acceptedImageSize);
      const image = await this.categoryApi
        .addImage(preparedImage, this.props.categoryId, this.props.defaultSelectedTemplateId);
      if (image) {
        this.props.addProcessingImage({
          fileId: file.fileId,
          id: image.id,
        });
      } else {
        this.props.deleteUploadedImage({
          fileId: file.fileId,
        });
      }
    }
    this.hiddenFileInput.current.value = '';
  }

  resize(loadingData, maxAcceptedSize) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (maxAcceptedSize) {
          const mainCanvas = document.createElement('canvas');
          let aspectRatio = img.width / img.height;
          mainCanvas.width = img.width;
          mainCanvas.height = img.height;

          if (img.width > maxAcceptedSize) {
            mainCanvas.width = maxAcceptedSize;
            mainCanvas.height = mainCanvas.width / aspectRatio;
          } else if (img.height > maxAcceptedSize) {
            aspectRatio = img.height / img.width;
            mainCanvas.height = maxAcceptedSize;
            mainCanvas.width = mainCanvas.height / aspectRatio;
          } else {
            resolve(loadingData);
          }

          const ctx = mainCanvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, mainCanvas.width, mainCanvas.height);
          mainCanvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg');
        } else {
          resolve(loadingData);
        }
      };

      img.src = URL.createObjectURL(loadingData);
    });
  }

  getPropOfDefaultTemplate(propName) {
    const defTemplate = this.props.templates.find((el) => {
      return el.id === this.props.defaultSelectedTemplateId;
    });

    return defTemplate ? defTemplate[propName] : '';
  }

  handleActiveTemplateChange(id) {
    this.props.setDefaultSelectedTemplate(id);
    this.setState({
      selectedTemplateId: id,
    });
  }

  render() {
    const {
      classes,
      className,
    } = this.props;

    return (
      <Card
        className={clsx(classes.root, className)}
      >
        <CardContent
          className={classes.cardContent}
        >
          <Box
            display="flex"
            flexDirection="column"
          >
            <div className={classes.uploadArea} onClick={this.handleClickUpload}>
              <CardMedia
                className={classes.media}
                image={this.getPropOfDefaultTemplate('icon_192')}
                title="Active template"
              >
                <input
                  type="file"
                  style={{ display: 'none' }}
                  ref={this.hiddenFileInput}
                  onChange={this.handleChange}
                  onClick={() => { sendMetric('click', 'clickOnUploadImage'); }}
                  multiple
                />
                <Box
                  className={classes.filesArea}
                  mb={3}
                >
                  <Typography
                    className={classes.textUpload}
                  >
                    <FormattedMessage id="label.upload.new.img" defaultMessage="UPLOAD NEW IMAGES"/>
                  </Typography>
                  <CloudUploadIcon
                    className={classes.uploadIcon}
                  />
                  <Typography
                    className={classes.textTemplateName}
                  >
                    {this.getPropOfDefaultTemplate('name') || ''}
                  </Typography>
                </Box>
              </CardMedia>
            </div>
            <div float="left">
              <Typography
                className={classes.textLatest}
              >
                <FormattedMessage id="label.latest.template" defaultMessage="Latest Templates"/>
              </Typography>
            </div>
            <Box
              display="flex"
              flexDirection="row"
              p={1}
              margin="0 auto"
            >
              {
                Object.keys(this.props.lastUsedTemplates).reverse().map((key) => (
                  <Box className={classes.tmpBox} key={this.props.lastUsedTemplates[key].name}>
                    <Avatar
                      className={classes.templateThumb}
                      variant="rounded"
                      src={this.props.lastUsedTemplates[key].icon_192}
                      square="true"
                      title="Click to Activate"
                      onClick={
                        () => this.handleActiveTemplateChange(this.props.lastUsedTemplates[key].id)
                      }
                    />
                    <Typography
                      className={classes.templateText}
                    >
                      {this.props.lastUsedTemplates[key].name}
                    </Typography>
                  </Box>
                ))
              }
              <TemplateListView />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }
}

UploadImageTemplateCard.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  categoryId: PropTypes.string,
  templates: PropTypes.array,
  defaultSelectedTemplateId: PropTypes.string,
  lastUsedTemplates: PropTypes.object,
  setLstUsedTemplate: PropTypes.func,
  setDefaultSelectedTemplate: PropTypes.func,
  addUploadedImage: PropTypes.func,
  deleteUploadedImage: PropTypes.func,
  addImages: PropTypes.func,
  addUploadingImages: PropTypes.func,
  updateCategoryCount: PropTypes.func,
};

export default connect(
  getTemplates,
  {
    setLstUsedTemplate,
    setDefaultSelectedTemplate,
    addUploadedImage,
    deleteUploadedImage,
    addProcessingImage,
    addUploadingImages,
    addProcessedImage,
    addImages,
    updateCategoryCount,
  }
)(withStyles(styles)(UploadImageTemplateCard));
